import { Link } from 'react-router-dom';
import { IconAlert } from '../ui/icons';
import { shortPackageRef } from './criticalAttentionFormat';

/** Max alert cards on dashboard rails; full queue lives on Exceptions. */
export const CRITICAL_ATTENTION_PREVIEW_LIMIT = 5;

/**
 * "Critical attention" panel. Consumes the EXISTING getDashboardAlerts payload
 * (items: { exception_id, code, package_id, severity, status }). No new API.
 */
const SEVERITY_TONE = {
  HIGH: 'border-l-rose-400 bg-rose-50/60',
  CRITICAL: 'border-l-rose-500 bg-rose-50/70',
  MEDIUM: 'border-l-amber-400 bg-amber-50/60',
  LOW: 'border-l-sky-400 bg-sky-50/50',
};

function AlertCard({ alert }) {
  const tone =
    SEVERITY_TONE[String(alert.severity || '').toUpperCase()] ||
    'border-l-slate-300 bg-eorig-surface';
  return (
    <li
      className={`rounded-lg border border-eorig-border border-l-[3px] p-2.5 ${tone}`}
    >
      <p className="text-[13px] font-semibold leading-snug text-eorig-ink">
        {alert.code || 'Exception'}
      </p>
      <p
        className="mt-0.5 text-[11px] leading-snug text-eorig-muted"
        title={alert.package_id ? `Package ${alert.package_id}` : undefined}
      >
        Package {shortPackageRef(alert.package_id)}
        {alert.severity ? ` · ${alert.severity}` : ''}
        {alert.status ? ` · ${alert.status}` : ''}
      </p>
      <Link
        to={`/exceptions/${alert.exception_id}`}
        className="mt-2 inline-flex items-center text-[12px] font-semibold text-eorig-navy-600 hover:text-eorig-cyan"
      >
        Resolve →
      </Link>
    </li>
  );
}

export function CriticalAttentionPanel({
  alerts,
  loading,
  viewAllHref = '/exceptions',
  maxPreview = CRITICAL_ATTENTION_PREVIEW_LIMIT,
  totalCount,
  maxHeightPx,
  className = '',
}) {
  const all = Array.isArray(alerts) ? alerts : [];
  const preview = maxPreview > 0 ? all.slice(0, maxPreview) : all;
  const openTotal = typeof totalCount === 'number' ? totalCount : all.length;
  const hidden = Math.max(0, openTotal - preview.length);

  const heightClass = maxHeightPx ? 'h-full max-h-full' : 'max-h-none xl:max-h-[min(380px,50vh)]';

  return (
    <section
      className={`relative z-0 flex ${heightClass} flex-col overflow-hidden rounded-2xl border border-eorig-border bg-white shadow-eorig-card ${className}`}
      style={maxHeightPx ? { maxHeight: `${maxHeightPx}px` } : undefined}
      aria-label="Critical attention"
    >
      <div className="flex shrink-0 items-center justify-between border-b border-eorig-border px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-rose-50 text-rose-600">
            <IconAlert className="h-4 w-4" />
          </span>
          <h2 className="text-sm font-semibold text-eorig-ink">
            Critical attention
          </h2>
        </div>
        <Link
          to={viewAllHref}
          className="text-[12px] font-semibold text-eorig-navy-600 hover:text-eorig-cyan"
        >
          View all
        </Link>
      </div>
      <ul className="m-0 min-h-0 flex-1 list-none space-y-2 overflow-y-auto p-3">
        {loading ? (
          <li className="px-1 py-6 text-center text-sm text-eorig-muted">
            Loading…
          </li>
        ) : preview.length ? (
          preview.map((alert) => (
            <AlertCard key={alert.exception_id} alert={alert} />
          ))
        ) : (
          <li className="rounded-xl bg-eorig-surface px-3 py-6 text-center text-sm text-eorig-muted">
            Nothing needs attention right now.
          </li>
        )}
      </ul>
      {!loading && hidden > 0 ? (
        <p className="shrink-0 border-t border-eorig-border px-4 py-2.5 text-center text-[12px] text-eorig-muted">
          {hidden} more open —{' '}
          <Link
            to={viewAllHref}
            className="font-semibold text-eorig-navy-600 hover:text-eorig-cyan"
          >
            View all
          </Link>
        </p>
      ) : null}
    </section>
  );
}
